/**
 * Planner bootstrap API — single composite cold-load endpoint.
 *
 * GET /api/v1/planner/{propertyId}/bootstrap
 *
 * Returns all 7 planner sub-payloads in one server-side parallel call,
 * reducing cold-load from 7 backend round-trips to ≤ 3.
 *
 * Each section is independently error-handled: if a sub-call fails on the
 * backend the section is null with an error code string; the endpoint always
 * returns 200 (never throws for sub-call failures).
 */

import { getStoredAuthAccessToken, simulationApiClient } from '@/api/client';
import type { PlannerSummaryResponse, ResolvedStateSummary } from '@/hooks/usePlannerData';
import type { PropertyOverviewData } from '@/types/forest-detailed';
import type { SyncStatus } from '@/api/timber-prices';
import type { BaselinePlanResponse } from '@/api/operationsPlan';
import { normalizeSavedPlans, type SavedPlan } from '@/api/savedPlans';

const inFlightBootstrapRequests = new Map<string, Promise<PlannerBootstrapResponse>>();
const AUTH_SESSION_EXPIRED_EVENT = 'lehti_ai:auth-session-expired';

declare global {
  interface Window {
    __fmPlannerBootstrapAuthListenerInstalled?: boolean;
  }
}

// Response shape

export interface PlannerBootstrapResponse {
  /** Planner summary (operations + economics). Null on sub-call failure. */
  planner_summary: PlannerSummaryResponse | null;
  planner_summary_error: string | null;

  /** Property overview (stands list + statistics). Null on sub-call failure. */
  property_overview: PropertyOverviewData | null;
  property_overview_error: string | null;

  /**
   * Batch resolved-state summaries for source-mode badges.
   * Shape: { property_id, summaries: ResolvedStateSummary[] }
   */
  resolved_state_summary: { property_id: string; summaries: ResolvedStateSummary[] } | null;
  resolved_state_summary_error: string | null;

  /** Action decisions list. Null on sub-call failure. */
  action_decisions: Array<{
    id: number;
    stand_id: string;
    recommendation_source: string;
    action_key: string;
    decision: string;
    note?: string;
    created_at?: string;
    updated_at?: string;
  }> | null;
  action_decisions_error: string | null;

  /** Timber-prices sync status (Django proxy). Null on sub-call failure. */
  sync_status: { data: SyncStatus } | null;
  sync_status_error: string | null;

  /** Metsäkeskus baseline plan (50-year horizon). Null on sub-call failure. */
  baseline_plan: BaselinePlanResponse | null;
  baseline_plan_error: string | null;

  /** Saved plans list for this property. Null on sub-call failure. */
  saved_plans: SavedPlan[] | null;
  saved_plans_error: string | null;
}

// Query params

export interface FetchPlannerBootstrapParams {
  /** BCP-47 language code (default: 'en'). */
  language?: string;
  /** LUKE pricing window months — 3 | 12 | 24 | 36 (default: 12). */
  priceWindowMonths?: number;
  /** LUKE region code (optional). */
  priceRegion?: string;
}

// API call

function bootstrapRequestKey(
  propertyId: string,
  queryParams: Record<string, string | number>,
): string {
  const params = new URLSearchParams();
  Object.entries(queryParams)
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([key, value]) => params.append(key, String(value)));

  return JSON.stringify({
    auth: currentBootstrapAuthFingerprint(),
    propertyId,
    query: params.toString(),
  });
}

function normalizeBootstrapResponse(data: PlannerBootstrapResponse): PlannerBootstrapResponse {
  return {
    ...data,
    saved_plans:
      data.saved_plans == null
        ? null
        : normalizeSavedPlans(data.saved_plans, 'PlannerBootstrap.saved_plans'),
  };
}

function currentBootstrapAuthFingerprint(): string {
  if (typeof localStorage === 'undefined') return 'server';

  const accessToken = getStoredAuthAccessToken();
  if (accessToken) {
    return `access:${stableAuthTokenFingerprint(accessToken)}`;
  }

  try {
    const rawUser = localStorage.getItem('auth_user');
    if (rawUser) {
      const user = JSON.parse(rawUser) as {
        id?: unknown;
        email?: unknown;
        username?: unknown;
      };
      const userId = typeof user.id === 'number' || typeof user.id === 'string' ? user.id : null;
      const email = typeof user.email === 'string' ? user.email : null;
      const username = typeof user.username === 'string' ? user.username : null;
      return `user:${userId ?? email ?? username ?? 'unknown'}`;
    }
  } catch {
    return 'auth-user-unparseable';
  }

  return 'anonymous';
}

function stableAuthTokenFingerprint(token: string): string {
  let hash = 2166136261;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash.toString(36);
}

export function clearPlannerBootstrapInFlightRequests(): void {
  inFlightBootstrapRequests.clear();
}

if (typeof window !== 'undefined' && !window.__fmPlannerBootstrapAuthListenerInstalled) {
  window.__fmPlannerBootstrapAuthListenerInstalled = true;
  window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, clearPlannerBootstrapInFlightRequests);
  window.addEventListener('storage', (event) => {
    if (event.key === 'auth_tokens' || event.key === 'auth_user') {
      clearPlannerBootstrapInFlightRequests();
    }
  });
}

/**
 * Fetch all planner bootstrap data in a single server-side composite call.
 *
 * The backend fires 7 sub-payloads in parallel via asyncio.gather.
 * This function always resolves (never throws): sub-call failures are
 * represented as null sections with error codes in the response.
 *
 * React Strict Mode can mount the Operations Planning route twice in local/dev
 * browser runs. Coalesce identical in-flight bootstrap calls so the first real
 * backend response is shared instead of letting the duplicate race into the
 * axios timeout and replace a good plan with an error state.
 */
export async function fetchPlannerBootstrap(
  propertyId: string,
  params?: FetchPlannerBootstrapParams,
): Promise<PlannerBootstrapResponse> {
  const queryParams: Record<string, string | number> = {};
  if (params?.language) queryParams.language = params.language;
  if (params?.priceWindowMonths != null) {
    queryParams.price_window_months = params.priceWindowMonths;
  }
  if (params?.priceRegion) queryParams.price_region = params.priceRegion;

  const key = bootstrapRequestKey(propertyId, queryParams);
  const existing = inFlightBootstrapRequests.get(key);
  if (existing) return existing;

  const request = simulationApiClient
    .get<PlannerBootstrapResponse>(
      `/api/v1/planner/${propertyId}/bootstrap`,
      { params: queryParams },
    )
    .then((response) => normalizeBootstrapResponse(response.data));

  inFlightBootstrapRequests.set(key, request);

  try {
    return await request;
  } finally {
    if (inFlightBootstrapRequests.get(key) === request) {
      inFlightBootstrapRequests.delete(key);
    }
  }
}
